import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
    Box, Typography, Paper, CircularProgress, Button, Divider, Container, Grid, Card, CardContent, Avatar, Chip, Stack, IconButton, Tooltip 
} from '@mui/material';
import { 
    ArrowBack as ArrowBackIcon,
    Person as PersonIcon,
    AccessTime as AccessTimeIcon,
    Email as EmailIcon,
    AddCircleOutline as AddCircleOutlineIcon,
    Reply as ReplyIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config';

function InboxDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [email, setEmail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchEmailDetails = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await axios.get(`${config}/api/inbox/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setEmail(response.data);
            } catch (err) {
                console.error("Error fetching email details:", err);
                setError("Failed to load email details.");
            } finally {
                setLoading(false);
            }
        };

        fetchEmailDetails();
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    if (error) return (
        <Container maxWidth="lg" sx={{ mt: 4 }}>
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography color="error" variant="h6" gutterBottom>{error}</Typography>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inbox')}>Back to Inbox</Button>
            </Paper>
        </Container>
    );
    if (!email) return <Box p={3}><Typography>Email not found</Typography><Button onClick={() => navigate('/inbox')}>Back to Inbox</Button></Box>;

    const handleCreateTicket = () => {
        // Parse "Name <email>" if possible, or just use raw string
        let requestedBy = email.sender;
        let senderEmail = '';
        
        // Simple regex to extract email if present in < >
        const emailMatch = email.sender.match(/<([^>]+)>/);
        if (emailMatch) {
            senderEmail = emailMatch[1];
            requestedBy = email.sender.replace(`<${senderEmail}>`, '').trim();
            requestedBy = requestedBy.replace(/"/g, ''); // Remove quotes if any
        } else if (email.sender.includes('@')) {
             senderEmail = email.sender;
        }

        // STRIP HTML TAGS for ticket description
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = email.body;
        const plainTextBody = tempDiv.textContent || tempDiv.innerText || "";

        navigate('/tickets', { 
            state: { 
                createFromEmail: {
                    title: email.subject,
                    description: plainTextBody,
                    requested_by: requestedBy,
                    sender_email: senderEmail
                } 
            } 
        });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ mb: 3 }}>
                <Button 
                    startIcon={<ArrowBackIcon />} 
                    onClick={() => navigate('/inbox')}
                    sx={{ mb: 2, color: 'text.secondary' }}
                >
                    Back to Inbox
                </Button>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a237e', width: '100%', mb: { xs: 2, md: 0 } }}>
                        {email.subject}
                    </Typography>
                    <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={handleCreateTicket}
                        size="large"
                        sx={{ borderRadius: 2, px: 3, whiteSpace: 'nowrap', alignSelf: { xs: 'flex-start', md: 'auto' } }}
                    >
                        Create Ticket
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Sender Details</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}>
                                    {email.sender.charAt(0).toUpperCase()}
                                </Avatar>
                                <Box sx={{ overflow: 'hidden' }}>
                                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
                                        {email.sender}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Divider sx={{ my: 2 }} />
                            
                            <Stack spacing={2}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Received</Typography>
                                        <Typography variant="body2">{email.date}</Typography>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <EmailIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                                        <Chip label="Received" size="small" color="success" variant="outlined" />
                                    </Box>
                                </Box>
                            </Stack>

                         
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card sx={{ borderRadius: 3, boxShadow: 2, minHeight: '60vh' }}>
                        <CardContent>
                             <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.secondary', mb: 2 }}>
                                Message Body
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            <Box sx={{ 
                                '& img': { maxWidth: '100%', height: 'auto' },
                                '& a': { color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } },
                                fontFamily: 'inherit',
                                overflowWrap: 'break-word',
                                wordWrap: 'break-word'
                            }}>
                                <div dangerouslySetInnerHTML={{ __html: email.body }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}

export default InboxDetails;
